<template>
  <section>
    <v-progress-linear v-if="session?.busy.value" indeterminate />
    <v-alert v-if="!session" type="info" variant="tonal"
      >Sign in to load your cart.</v-alert
    >
    <template v-else>
      <v-alert
        v-if="!session.orderRef.value && !session.busy.value"
        type="info"
        variant="tonal"
      >
        Your cart is empty. Add an item to get started.
      </v-alert>
      <v-alert
        v-else-if="!session.ready.value && !session.busy.value"
        type="warning"
        variant="tonal"
      >
        Your cart is not available. Refresh to retry.
        <v-btn variant="text" @click="refresh">Retry</v-btn>
      </v-alert>
      <OrderReceipt
        v-if="session.ready.value && session.order.value"
        :order="session.order.value"
        :lines="session.order.value.lineItems ?? []"
        :item-title="(line) => itemTitle(line.itemInstanceIdTargetIds.assignId)"
        purchaser-is-viewer
        :summary="summary"
        :line-details="lineDetails"
        :authorized-cents="
          session.attempts.value.reduce(
            (sum, payment) =>
              sum + (payment.status === 'CAPTURABLE' ? payment.amountCents : 0),
            0,
          )
        "
      >
        <template #line-actions="{ line }">
          <v-btn
            icon="mdi-close"
            size="small"
            variant="text"
            aria-label="Remove item"
            :disabled="!session.isOpen.value || session.busy.value"
            :loading="removingLineId === line.assignId"
            @click="remove(line.assignId)"
          />
        </template>
      </OrderReceipt>
    </template>
  </section>
</template>

<script lang="ts" setup>
import { useCatalogDeferred } from "@/composables/shop/useCatalog";
import {
  memberOrderSessionKey,
  type MemberOrderSession,
} from "@/composables/shop/useMemberOrder";
import { useNotificationStore } from "@/stores/notification";
import type { LineItemRef } from "../../../cbi-generator/generated/dto/LineItemRef";
import OrderReceipt from "./OrderReceipt.vue";
import {
  loadReceiptLineDetails,
  type ReceiptLineDetails,
} from "@/composables/shop/receiptLineDetails";

const session = inject<MemberOrderSession | null>(memberOrderSessionKey, null);
/** Show payments applied and balance due instead of subtotal and total. */
defineProps<{ summary?: boolean }>();
const catalog = useCatalogDeferred();
const notification = useNotificationStore();
const removingLineId = ref<number | null>(null);
const lineDetails = shallowRef<Record<number, ReceiptLineDetails>>({});

// Reload after every cart load; details are optional, so failures only hide them.
let detailsVersion = 0;
watch(
  () => [session?.loads.value, session?.orderRef.value?.assignId] as const,
  async ([, assignId]) => {
    const current = ++detailsVersion;
    if (assignId == null) {
      lineDetails.value = {};
      return;
    }
    try {
      const details = await loadReceiptLineDetails({ assignId }, "Member");
      if (current === detailsVersion) lineDetails.value = details;
    } catch {
      if (current === detailsVersion) lineDetails.value = {};
    }
  },
  { immediate: true },
);

function itemTitle(instanceId: number) {
  return (
    catalog.value?.getProductFromItemInstance(instanceId)?.title ??
    `Catalog item #${instanceId}`
  );
}

async function refresh() {
  if (!session) return;
  try {
    await session.open();
  } catch (error) {
    notification.addSnackbar(
      error instanceof Error ? error.message : "Unable to refresh the cart.",
      "error",
    );
  }
}

async function remove(assignId: number) {
  if (!session) return;
  removingLineId.value = assignId;
  try {
    const line: LineItemRef = { assignId };
    await session.remove(line);
  } catch (error) {
    notification.addSnackbar(
      error instanceof Error ? error.message : "Unable to remove this item.",
      "error",
    );
  } finally {
    removingLineId.value = null;
  }
}
</script>
